import { z } from 'zod';

/**
 * Reference research: collections (images harvested from a page, symbols cut from them, drawings
 * rendered from path data, similarity groups, review sheets) and durable multi-lane research runs.
 * Consumed by the desktop engine (`/collections`, `/research/runs`) and its MCP tools.
 */

const isPattern = (value: string) => { try { new RegExp(value, 'i'); return true; } catch { return false; } };
const isHttps = (value: string) => {
  try { const url = new URL(value); return url.protocol === 'https:' && !url.username && !url.password; } catch { return false; }
};

/** A name inside the library: lowercase letters, digits and hyphens. */
export const ResearchSlugSchema = z.string().regex(/^[a-z0-9][a-z0-9-]{0,62}$/, 'Use lowercase letters, digits and hyphens');
export const ResearchHttpsUrlSchema = z.string().max(2048).refine(isHttps, 'Use a public HTTPS address');
const PatternSchema = z.string().min(1).max(200).refine(isPattern, 'Not a valid pattern');
const HexColourSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Use a colour such as #111111');
const ItemIdSchema = z.string().min(1).max(200);
const LabelValueSchema = z.union([z.string().max(4000), z.number(), z.boolean(), z.null(), z.array(z.string().max(400)).max(50)]);

export const CollectionItemKindSchema = z.enum(['image', 'symbol', 'drawing']);

export const CollectionCreateSchema = z.object({
  slug: ResearchSlugSchema,
  title: z.string().trim().min(1).max(160),
  note: z.string().trim().max(2000).optional(),
}).strict();

/** Harvest one page: its content images with their heading and section text. */
export const CollectionHarvestSchema = z.object({
  page: ResearchHttpsUrlSchema,
  include: z.array(PatternSchema).max(20).optional(),
  exclude: z.array(PatternSchema).max(40).optional(),
  maxImages: z.number().int().min(1).max(500).optional(),
}).strict();

/** Labels per item id; a null value removes that label, a null item clears all its labels. */
export const CollectionLabelSchema = z.object({
  labels: z.record(ItemIdSchema, z.record(z.string().min(1).max(64), LabelValueSchema).nullable()),
}).strict();

export const CutOptionsSchema = z.object({
  ink: z.enum(['auto', 'light', 'dark']).optional(),
  region: z.enum(['all', 'left-panel']).optional(),
  panelAspect: z.number().min(1).max(20).optional(),
  gap: z.number().int().min(0).max(20).optional(),
  reach: z.number().int().min(0).max(50).optional(),
  satellite: z.number().min(0).max(1).optional(),
  minArea: z.number().int().min(1).max(100000).optional(),
  pad: z.number().int().min(0).max(50).optional(),
  scale: z.number().int().min(1).max(8).optional(),
  color: HexColourSchema.optional(),
  edgeBand: z.number().int().min(0).max(100).optional(),
}).strict();

/** Cut symbols out of the collection's images (all, or `ids`); options by source site or by image. */
export const CollectionCutSchema = z.object({
  ids: z.array(ItemIdSchema).max(2000).optional(),
  options: CutOptionsSchema.optional(),
  bySource: z.record(z.string().min(1).max(200), CutOptionsSchema).optional(),
  byItem: z.record(ItemIdSchema, CutOptionsSchema).optional(),
}).strict();

/** Render path data (one filled path per drawing) to transparent PNGs, with the SVG beside each. */
export const CollectionDrawSchema = z.object({
  source: ResearchSlugSchema,
  paths: z.array(z.object({
    id: z.string().regex(/^[\w.-]{1,80}$/, 'Use letters, digits, dots, hyphens and underscores'),
    d: z.string().min(1).max(500000),
    viewBox: z.tuple([z.number(), z.number(), z.number().positive(), z.number().positive()]).optional(),
    label: z.string().max(200).optional(),
  }).strict()).min(1).max(5000),
  size: z.number().int().min(8).max(4096).optional(),
  color: HexColourSchema.optional(),
  fillRule: z.enum(['evenodd', 'nonzero']).optional(),
  supersample: z.number().int().min(1).max(8).optional(),
}).strict();

export const SimilarOptionsSchema = z.object({
  duplicate: z.number().min(0).max(1).optional(),
  duplicateAcross: z.number().min(0).max(1).optional(),
  group: z.number().min(0).max(1).optional(),
  aspect: z.number().min(0).max(5).optional(),
  keep: z.enum(['largest', 'first']).optional(),
  prefer: z.string().min(1).max(200).optional(),
}).strict();

/** Find duplicates and close variants among the collection's items; `prune` marks redundant copies removed. */
export const CollectionSimilarSchema = z.object({
  name: ResearchSlugSchema.default('similar'),
  kinds: z.array(CollectionItemKindSchema).min(1).default(['symbol']),
  sources: z.array(z.string().min(1).max(200)).max(50).optional(),
  includeRemoved: z.boolean().default(false),
  prune: z.boolean().default(false),
  options: SimilarOptionsSchema.default({}),
}).strict();

/** A contact sheet of items (by id or kind) or of a similarity result's groups, one row per group. */
export const CollectionSheetSchema = z.object({
  name: ResearchSlugSchema,
  ids: z.array(ItemIdSchema).max(3000).optional(),
  kind: CollectionItemKindSchema.optional(),
  similarity: z.object({
    name: ResearchSlugSchema,
    kind: z.enum(['duplicate', 'similar', 'all']).default('all'),
    limit: z.number().int().min(1).max(3000).optional(),
  }).strict().optional(),
  columns: z.number().int().min(1).max(12).optional(),
  cell: z.number().int().min(32).max(512).optional(),
}).strict();

/** One lane of a research run: a scope searched and read by its own agent loop. */
export const ResearchLaneSchema = z.object({
  id: ResearchSlugSchema,
  title: z.string().trim().min(1).max(160),
  brief: z.string().trim().min(1).max(8000),
  seeds: z.array(ResearchHttpsUrlSchema).max(20).optional(),
  queries: z.array(z.string().trim().min(1).max(200)).max(20).optional(),
}).strict();

export const ResearchSearchSettingsSchema = z.object({
  /** Wikipedia language editions searched without a key. */
  wikipedia: z.array(z.string().regex(/^[a-z]{2,3}$/)).min(1).max(6).default(['en']),
  /** Broad web search: off, the configured Brave key, or the model's own web search (Grok). */
  web: z.enum(['off', 'brave', 'model']).default('off'),
}).strict();

export const ResearchBudgetSchema = z.object({
  steps: z.number().int().min(1).max(600).default(120),
  searches: z.number().int().min(1).max(300).default(40),
  pages: z.number().int().min(1).max(300).default(60),
  records: z.number().int().min(1).max(500).default(120),
}).strict();

/**
 * Start a run. `recordSchema` is the JSON Schema of one record (an object); every saved record is
 * validated against it and must cite only sources the lane opened. `recordKey` names the field
 * that identifies a record.
 */
export const ResearchRunCreateSchema = z.object({
  title: z.string().trim().min(1).max(160),
  brief: z.string().trim().min(1).max(20000),
  recordSchema: z.record(z.string(), z.unknown()),
  recordKey: z.string().min(1).max(64).default('id'),
  sourcesField: z.string().min(1).max(64).default('sources'),
  lanes: z.array(ResearchLaneSchema).min(1).max(8),
  connectionId: z.string().min(1).max(80).optional(),
  search: ResearchSearchSettingsSchema.default({ wikipedia: ['en'], web: 'off' }),
  budget: ResearchBudgetSchema.default({ steps: 120, searches: 40, pages: 60, records: 120 }),
}).strict();

export const ResearchLaneStateSchema = z.enum(['waiting', 'running', 'done', 'stopped', 'failed', 'interrupted']);

export type CollectionCreate = z.infer<typeof CollectionCreateSchema>;
export type CollectionHarvest = z.infer<typeof CollectionHarvestSchema>;
export type CollectionLabel = z.infer<typeof CollectionLabelSchema>;
export type CutOptions = z.infer<typeof CutOptionsSchema>;
export type CollectionCut = z.infer<typeof CollectionCutSchema>;
export type CollectionDraw = z.infer<typeof CollectionDrawSchema>;
export type SimilarOptions = z.infer<typeof SimilarOptionsSchema>;
export type CollectionSimilar = z.infer<typeof CollectionSimilarSchema>;
export type CollectionSheet = z.infer<typeof CollectionSheetSchema>;
export type ResearchLane = z.infer<typeof ResearchLaneSchema>;
export type ResearchRunCreate = z.infer<typeof ResearchRunCreateSchema>;
export type ResearchLaneState = z.infer<typeof ResearchLaneStateSchema>;
